import { NavLink, useNavigate } from "react-router-dom";
import {
  Activity,
  Bookmark,
  CreditCard,
  Database,
  Gauge,
  HelpCircle,
  History,
  Leaf,
  LayoutDashboard,
  LogOut,
  Plug,
  Radar,
  Settings as SettingsIcon,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ImpersonationBanner, UserMenu } from "@/components/layout/UserMenu";
import { dateTime } from "@/lib/format";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/nieuwe-scan", label: "Nieuwe Scan", icon: Radar, testid: "nav-new-scan" },
  { to: "/handelskansen", label: "Handelskansen", icon: TrendingUp, testid: "nav-opportunities" },
  { to: "/watchlist", label: "Watchlist", icon: Bookmark, testid: "nav-watchlist" },
  { to: "/scanhistorie", label: "Scanhistorie", icon: History, testid: "nav-scan-history" },
  { to: "/marktdata", label: "Marktdata", icon: Database, testid: "nav-market-data" },
  { to: "/co2-referentie", label: "CO₂ Referentie", icon: Leaf, testid: "nav-co2-reference" },
  { to: "/instellingen", label: "Instellingen", icon: SettingsIcon, testid: "nav-settings" },
  { to: "/integraties", label: "Integraties", icon: Plug, testid: "nav-integrations" },
  { to: "/abonnement", label: "Abonnement", icon: CreditCard, testid: "nav-subscription" },
  { to: "/help", label: "Help & Uitleg", icon: HelpCircle, testid: "nav-help" },
];

export const AppShell = ({ children, title, subtitle, actions, freshness }) => {
  const { user, logout, isMaster } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 lg:flex">
      <aside className="hidden lg:flex lg:w-56 lg:flex-col border-r border-slate-800 bg-slate-900">
        <div className="flex items-center gap-2 border-b border-slate-800 px-4 py-3.5">
          <Gauge className="h-5 w-5 text-blue-400" strokeWidth={2.2} />
          <div className="leading-tight">
            <div className="font-display text-[15px] font-bold tracking-tight text-white">
              ImDash
            </div>
            <div className="font-display text-[10px] font-semibold uppercase tracking-[0.22em] text-blue-400">
              Trade Intelligence
            </div>
          </div>
        </div>
        <nav className="flex-1 space-y-0.5 p-2" data-testid="sidebar-nav">
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testid}
              className={({ isActive }) =>
                `ati-sidebar-link flex items-center gap-2.5 rounded-sm px-2.5 py-2 text-[13px] font-medium ${
                  isActive
                    ? "bg-blue-600 text-white"
                    : "text-slate-300 hover:bg-slate-800 hover:text-white"
                }`
              }
            >
              <Icon className="h-4 w-4" strokeWidth={2} />
              {label}
            </NavLink>
          ))}
          {isMaster && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `ati-sidebar-link mt-2 flex items-center gap-2.5 rounded-sm border-t border-slate-800 px-2.5 py-2 pt-3 text-[13px] font-medium ${
                  isActive ? "text-amber-300" : "text-amber-400 hover:bg-slate-800"
                }`
              }
            >
              <ShieldCheck className="h-4 w-4" strokeWidth={2} />
              Master Admin
            </NavLink>
          )}
        </nav>
        <div className="border-t border-slate-800 p-3">
          <div className="mb-2 truncate text-[11px] text-slate-400">{user?.email}</div>
          <button
            type="button"
            data-testid="logout-button"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            className="flex w-full items-center gap-2 rounded-sm border border-slate-700 px-2.5 py-1.5 text-[12px] font-medium text-slate-300 transition-colors duration-150 hover:border-slate-600 hover:bg-slate-800 hover:text-white"
          >
            <LogOut className="h-3.5 w-3.5" /> Uitloggen
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <ImpersonationBanner />
        <header className="sticky top-0 z-30 border-b border-slate-200 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <div className="min-w-0">
              <h1
                className="truncate font-display text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl"
                data-testid="page-title"
              >
                {title}
              </h1>
              {subtitle && <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {actions}
              <UserMenu />
            </div>
          </div>
          {freshness && (
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 border-t border-slate-100 bg-slate-50 px-4 py-1.5">
              {freshness.map((item) => (
                <span key={item.label} className="label-xs">
                  {item.label}:{" "}
                  <span className="mono font-normal normal-case tracking-normal text-slate-700">
                    {dateTime(item.value)}
                  </span>
                </span>
              ))}
            </div>
          )}
        </header>

        <nav className="flex gap-1 overflow-x-auto border-b border-slate-200 bg-slate-900 px-2 py-1.5 lg:hidden">
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={`m-${testid}`}
              className={({ isActive }) =>
                `flex shrink-0 items-center gap-1.5 rounded-sm px-2.5 py-1.5 text-[12px] font-medium ${
                  isActive ? "bg-blue-600 text-white" : "text-slate-300"
                }`
              }
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </NavLink>
          ))}
        </nav>

        <main className="flex-1 p-3 sm:p-4">{children}</main>

        <footer className="flex items-center gap-2 border-t border-slate-200 bg-white px-4 py-2 text-[11px] text-slate-500">
          <Activity className="h-3.5 w-3.5 text-slate-400" />
          ImDash — beslissingsondersteuning. Alle waarden zijn indicaties op basis van opgeslagen
          marktdata: geen aankoopadvies en geen boekhoudkundige nettowinst.
        </footer>
      </div>
    </div>
  );
};
